import { useEffect } from 'react';

const cache = new Map();

// Favor a recurring colorful region instead of black letterboxing or white logos.
export function artworkHue(pixels) {
  const buckets = Array.from({ length: 24 }, () => ({ weight: 0, hue: 0 }));
  for (let i = 0; i < pixels.length; i += 4) {
    const [r, g, b] = [pixels[i], pixels[i + 1], pixels[i + 2]].map(value => value / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
    if (pixels[i + 3] < 128 || delta < .08 || max < .15 || min > .88) continue;
    let hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
    hue = (hue * 60 + 360) % 360;
    const weight = delta * (1 - Math.abs((max + min) / 2 - .5));
    const bucket = buckets[Math.floor(hue / 15)];
    bucket.weight += weight;
    bucket.hue += hue * weight;
  }
  const best = buckets.reduce((a, b) => a.weight >= b.weight ? a : b);
  return best.weight ? Math.round(best.hue / best.weight) : null;
}

function loadHue(path) {
  if (cache.has(path)) return cache.get(path);
  const promise = new Promise(resolve => {
    const image = new Image();
    const timer = setTimeout(() => finish(null), 8000);
    const finish = hue => { clearTimeout(timer); image.onload = null; image.onerror = null; resolve(hue); };
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 40;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, 40, 40);
        finish(artworkHue(context.getImageData(0, 0, 40, 40).data));
      } catch { finish(null); }
    };
    image.onerror = () => finish(null);
    image.src = `https://image.tmdb.org/t/p/w300${path}`;
  });
  if (cache.size >= 80) cache.delete(cache.keys().next().value);
  cache.set(path, promise);
  return promise;
}

export default function useArtworkTheme(path) {
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    const root = document.documentElement;
    const updateChrome = () => document.querySelector('meta[name="theme-color"]')?.setAttribute('content', getComputedStyle(root).getPropertyValue('--bg').trim());
    const observer = new MutationObserver(updateChrome);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    loadHue(path).then(hue => {
      if (cancelled || hue === null) return;
      root.style.setProperty('--art-hue', hue);
      root.setAttribute('data-artwork', 'true');
      updateChrome();
    });
    return () => {
      cancelled = true;
      observer.disconnect();
      root.removeAttribute('data-artwork');
      root.style.removeProperty('--art-hue');
      updateChrome();
    };
  }, [path]);
}
